import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Divider,
    InputAdornment,
    Card,
    CardContent,
    Grid,
    Breadcrumbs,
    CircularProgress,
    Tooltip,
    IconButton,
} from "@mui/material";
import { privateReq } from "../../config/axiosConfig";
import toast from "react-hot-toast";
import Header from "../../components/Header";
import ScrollToTop from "../../components/ScrollToTop.js";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SaveIcon from "@mui/icons-material/Save";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Link } from "react-router-dom";

const PropertiesCredit = ({
    closeLoginModal,
    setCloseLoginModal,
    setOpenPopUp,
}) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [creditSettings, setCreditSettings] = useState({
        per_property: 1,
        price: 99,
        get_credit: 10
    });
    const [formData, setFormData] = useState({
        per_property: 1,
        price: 99,
        get_credit: 10
    });

    const headerRef = useRef();
    const [headerHeight, setHeaderHeight] = useState(0);

    useEffect(() => {
        fetchCreditSystemSettings();
        setHeaderHeight(headerRef.current.offsetHeight);
    }, []);

    const fetchCreditSystemSettings = async () => {
        setLoading(true);
        try {
            const response = await privateReq.get("/credit-system");
            const settings = response.data.data;
            setCreditSettings(settings);
            setFormData(settings);
        } catch (err) {
            console.error("Error fetching credit system settings:", err);
            toast.error("Failed to fetch credit system settings");
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow positive numbers
        if (value >= 0) {
        setFormData((prev) => ({
            ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const response = await privateReq.put("/credit-system", formData);
            setCreditSettings(response.data.data);
            toast.success("Credit system settings updated successfully");
        } catch (err) {
            console.error("Error updating credit system settings:", err);
            toast.error(err.response?.data?.message || "Failed to update settings");
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <ScrollToTop />
            <Header
                closeLoginModal={closeLoginModal}
                setCloseLoginModal={setCloseLoginModal}
                setOpenPopUp={setOpenPopUp}
                innerRef={headerRef}
            />

            <div style={{ marginLeft: "15px", marginTop: "1px", marginBottom: "10px" }}>
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" to="/admin">
                        Dashboard
                    </Link>

                    <Link underline="hover" color="inherit" to="/admin-settings">
                        Admin Setting
                    </Link>

                    <Typography sx={{ color: "text.primary" }}>
                        Credit System Management
                    </Typography>
                </Breadcrumbs>
            </div>
            <Box
                sx={{
                    p: { xs: 2, md: 4 },
                    maxWidth: "100%",
                    mt: `${headerHeight}px`,
                    background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)",
                    minHeight: "calc(100vh - 64px)",
                }}
            >
                <Grid container spacing={3}>
                    {/* Credit System Summary */}
                    <Grid item xs={12} md={4}>
                        <Card 
                    elevation={3}
                    sx={{
                                height: "100%",
                                borderRadius: "16px",
                                background: "linear-gradient(135deg, #ffffff 0%, #f9fafc 100%)",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                        overflow: "hidden",
                    }}
                >
                            <CardContent sx={{ p: 0 }}>
                    <Box
                        sx={{
                                        p: 3, 
                                        bgcolor: "#1976d2", 
                                        color: "white",
                                        background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                                        <CreditCardIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" component="div" fontWeight="600">
                                            Credit System Overview
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        Current credit system configuration
                                    </Typography>
                                </Box>
                                
                                <Box sx={{ p: 3 }}>
                                    {loading ? (
                                        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                                            <CircularProgress size={40} />
                                        </Box>
                                    ) : (
                                        <>
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Credits Required Per Property
                                                </Typography>
                                                <Typography variant="h4" component="div" color="primary" fontWeight="600">
                                                    {creditSettings.per_property}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Price Per Credit Package
                                                </Typography>
                                                <Typography variant="h4" component="div" color="primary" fontWeight="600">
                                                ₹{creditSettings.price}
                                                </Typography>
                                            </Box>
                                            
                                            <Box>
                                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                                    Credits Per Package
                                                </Typography>
                                                <Typography variant="h4" component="div" color="primary" fontWeight="600">
                                                    {creditSettings.get_credit}
                            </Typography>
                        </Box>
                                        </>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Credit System Settings Form */}
                    <Grid item xs={12} md={8}>
                        <Paper
                            elevation={3}
                            sx={{
                                width: "100%",
                                borderRadius: "16px",
                                background: "white",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                overflow: "hidden",
                            }}
                        >
                            <Box
                                                        sx={{
                                    p: 3,
                                    borderBottom: "1px solid #eee",
                                    background: "linear-gradient(135deg, #ffffff 0%, #f9fafc 100%)",
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <MonetizationOnIcon sx={{ fontSize: 24, color: "#1976d2", mr: 1 }} />
                                    <Typography variant="h6" fontWeight="600" color="#2c3e50">
                                        Credit System Settings
                                                            </Typography>
                                                    </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Adjust credit pricing and requirements for properties
                            </Typography>
                        </Box>

                        <form onSubmit={handleSubmit}>
                                <Box sx={{ p: 3 }}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={4}>
                            <TextField
                                fullWidth
                                                label="Credits Per Property"
                                                name="per_property"
                                                type="number"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CreditCardIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Number of credits required to list a single property">
                                                                <IconButton size="small">
                                                                    <HelpOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                value={formData.per_property}
                                onChange={handleInputChange}
                                margin="normal"
                                required
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "12px",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Price Per Package"
                                                name="price"
                                                type="number"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            ₹
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Cost of a single credit package">
                                                                <IconButton size="small">
                                                                    <HelpOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                value={formData.price}
                                                onChange={handleInputChange}
                                                margin="normal"
                                                required
                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "12px",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="Credits Per Package"
                                                name="get_credit"
                                                type="number"
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <CreditCardIcon fontSize="small" />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <Tooltip title="Number of credits received when purchasing a package">
                                                                <IconButton size="small">
                                                                    <HelpOutlineIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                                value={formData.get_credit}
                                                onChange={handleInputChange}
                                                margin="normal"
                                                required
                                                sx={{
                                                    "& .MuiOutlinedInput-root": {
                                                        borderRadius: "12px",
                                                    },
                                                }}
                                            />
                                        </Grid>
                                    </Grid>

                                    <Divider sx={{ my: 3 }} />

                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">
                                                Per credit cost: ₹{formData.price / formData.get_credit > 0 ? (formData.price / formData.get_credit).toFixed(2) : 0}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Cost per property: ₹{formData.per_property * (formData.price / formData.get_credit) > 0 ? (formData.per_property * (formData.price / formData.get_credit)).toFixed(2) : 0}
                                            </Typography>
                                        </Box>

                            <Button
                                type="submit"
                                variant="contained"
                                            disabled={saving || loading}
                                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                sx={{
                                    height: "48px",
                                    borderRadius: "12px",
                                    textTransform: "none",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                                background: "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                                    boxShadow: "0 3px 15px rgba(33,150,243,0.3)",
                                    "&:hover": {
                                                    background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                                    },
                                }}
                            >
                                            {saving ? "Saving..." : "Save Changes"}
                            </Button>
                                    </Box>
                                </Box>
                        </form>
                    </Paper>

                        {/* Pricing Card */}
                        <Card 
                            elevation={3}
                            sx={{
                                mt: 3,
                                borderRadius: "16px",
                                background: "linear-gradient(135deg, #ffffff 0%, #f9fafc 100%)",
                                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                                overflow: "hidden",
                            }}
                        >
                            <CardContent>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <MonetizationOnIcon sx={{ fontSize: 24, color: "#1976d2", mr: 1 }} />
                                    <Typography variant="h6" fontWeight="600" color="#2c3e50">
                                        Credit Package Pricing
                                    </Typography>
                                </Box>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <Box 
                                            sx={{ 
                                                bgcolor: "#f8fafc", 
                                                p: 2, 
                                                borderRadius: "12px",
                                                border: "1px solid #eaeef2"
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                                Current Package
                                            </Typography>
                                            <Typography variant="h5" color="primary" fontWeight="700" sx={{ mb: 1 }}>
                                            ₹{formData.price}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Get {formData.get_credit} credits
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                List up to {Math.floor(formData.get_credit / formData.per_property)} properties
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Box 
                                            sx={{ 
                                                bgcolor: "#ebf5ff", 
                                                p: 2, 
                                                borderRadius: "12px",
                                                border: "1px solid #d0e7ff"
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                                Cost Breakdown
                                            </Typography>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                                <Typography variant="body2">Per Credit Cost:</Typography>
                                                <Typography variant="body2" fontWeight="600">
                                                ₹{formData.price / formData.get_credit > 0 ? (formData.price / formData.get_credit).toFixed(2) : 0}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                                <Typography variant="body2">Property Listing Cost:</Typography>
                                                <Typography variant="body2" fontWeight="600">
                                                ₹{formData.per_property * (formData.price / formData.get_credit) > 0 ? (formData.per_property * (formData.price / formData.get_credit)).toFixed(2) : 0}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default PropertiesCredit;
